import React, {
  createContext, useContext, useState, useEffect, useCallback,
} from 'react';

const AuthContext = createContext(null);

const STORAGE_USERS_KEY = 'algodoor_users';
const STORAGE_CURRENT_USER_KEY = 'algodoor_current_user';
const STORAGE_PENDING_KEY = 'algodoor_pending_users';

const DEFAULT_ADMIN = {
  id: 1,
  username: 'admin',
  email: 'admin@algodoor.com',
  password: 'admin',
  role: 'admin',
  status: 'active',
  createdAt: new Date('2024-01-01').toISOString(),
};

const initUsers = () => {
  try {
    const stored = localStorage.getItem(STORAGE_USERS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Ensure admin always exists
      if (!parsed.find((u) => u.username === 'admin')) {
        const updated = [DEFAULT_ADMIN, ...parsed];
        localStorage.setItem(STORAGE_USERS_KEY, JSON.stringify(updated));
        return updated;
      }
      return parsed;
    }
  } catch (_) { /* ignore */ }
  const initial = [DEFAULT_ADMIN];
  localStorage.setItem(STORAGE_USERS_KEY, JSON.stringify(initial));
  return initial;
};

const initPending = () => {
  try {
    const stored = localStorage.getItem(STORAGE_PENDING_KEY);
    if (stored) return JSON.parse(stored);
  } catch (_) { /* ignore */ }
  return [];
};

export const AuthProvider = ({ children }) => {
  const [users, setUsers] = useState(initUsers);
  const [pendingUsers, setPendingUsers] = useState(initPending);
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_CURRENT_USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (_) { return null; }
  });

  // Persist users to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(STORAGE_USERS_KEY, JSON.stringify(users));
  }, [users]);

  useEffect(() => {
    localStorage.setItem(STORAGE_PENDING_KEY, JSON.stringify(pendingUsers));
  }, [pendingUsers]);

  useEffect(() => {
    if (currentUser) {
      localStorage.setItem(STORAGE_CURRENT_USER_KEY, JSON.stringify(currentUser));
    } else {
      localStorage.removeItem(STORAGE_CURRENT_USER_KEY);
    }
  }, [currentUser]);

  const login = useCallback((usernameOrEmail, password) => {
    const found = users.find(
      (u) => (u.username === usernameOrEmail || u.email === usernameOrEmail)
        && u.password === password
        && u.status === 'active',
    );
    if (!found) {
      throw new Error('Invalid credentials or account not yet approved.');
    }
    const { password: _pw, ...safeUser } = found;
    setCurrentUser(safeUser);
    return safeUser;
  }, [users]);

  const logout = useCallback(() => {
    setCurrentUser(null);
  }, []);

  const signup = useCallback((username, email, password) => {
    const userExists = users.find((u) => u.username === username || u.email === email);
    const pendingExists = pendingUsers.find((u) => u.username === username || u.email === email);
    if (userExists || pendingExists) {
      throw new Error('Username or email already taken.');
    }
    const newPending = {
      id: Date.now(),
      username,
      email,
      password,
      role: 'student',
      status: 'pending',
      createdAt: new Date().toISOString(),
    };
    setPendingUsers((prev) => [...prev, newPending]);
  }, [users, pendingUsers]);

  const approveUser = useCallback((pendingId, role) => {
    const pending = pendingUsers.find((u) => u.id === pendingId);
    if (!pending) return;
    const approvedUser = { ...pending, role, status: 'active' };
    setUsers((prev) => [...prev, approvedUser]);
    setPendingUsers((prev) => prev.filter((u) => u.id !== pendingId));
  }, [pendingUsers]);

  const rejectUser = useCallback((pendingId) => {
    setPendingUsers((prev) => prev.filter((u) => u.id !== pendingId));
  }, []);

  const updateUserRole = useCallback((userId, role) => {
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role } : u)));
    // If the updated user is current user, update session
    setCurrentUser((prev) => (prev && prev.id === userId ? { ...prev, role } : prev));
  }, []);

  const isAdmin = currentUser?.role === 'admin';
  const isAuthenticated = !!currentUser;

  return (
    <AuthContext.Provider value={{
      currentUser,
      isAuthenticated,
      isAdmin,
      users,
      pendingUsers,
      login,
      logout,
      signup,
      approveUser,
      rejectUser,
      updateUserRole,
    }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};

export default AuthContext;
